import type { StyleProp, TextStyle } from 'react-native';

/**
 * Real Hanken Grotesk weight faces registered in `_layout.tsx` via
 * `@expo-google-fonts/hanken-grotesk`. Each weight is a distinct family name
 * because the package ships static per-weight files (no variable font), and on
 * React Native `fontWeight` does not select a different registered file.
 */
export const HANKEN_FAMILY = {
  regular: 'HankenGrotesk_400Regular',
  medium: 'HankenGrotesk_500Medium',
  semibold: 'HankenGrotesk_600SemiBold',
  bold: 'HankenGrotesk_700Bold',
} as const;

/**
 * Tailwind fontWeight utilities → per-weight family tokens (see tailwind.config.js).
 * Only these exact tokens are swapped; `font-mono`, `font-sans`, and the
 * `font-sans-*` family tokens are intentionally left alone.
 */
const WEIGHT_CLASS_TO_FAMILY_CLASS: Record<string, string> = {
  'font-normal': 'font-sans',
  'font-medium': 'font-sans-medium',
  'font-semibold': 'font-sans-semibold',
  'font-bold': 'font-sans-bold',
};

/**
 * Rewrite weight utility classes to real per-weight family tokens so text
 * renders in an actual Hanken weight face instead of a synthetic weight.
 */
export function resolveWeightClasses(className: string): string {
  if (!className || !className.trim()) return '';
  return className
    .split(/\s+/)
    .filter(Boolean)
    .map((token) => WEIGHT_CLASS_TO_FAMILY_CLASS[token] ?? token)
    .join(' ');
}

function familyForWeight(weight: TextStyle['fontWeight']): string {
  if (weight === undefined || weight === null) return HANKEN_FAMILY.regular;
  let numeric: number;
  if (typeof weight === 'number') {
    numeric = weight;
  } else if (weight === 'bold') {
    numeric = 700;
  } else if (weight === 'normal') {
    numeric = 400;
  } else {
    numeric = parseInt(weight, 10);
  }
  if (Number.isNaN(numeric)) return HANKEN_FAMILY.regular;
  if (numeric >= 700) return HANKEN_FAMILY.bold;
  if (numeric >= 600) return HANKEN_FAMILY.semibold;
  if (numeric >= 500) return HANKEN_FAMILY.medium;
  return HANKEN_FAMILY.regular;
}

/**
 * Map an inline `fontWeight` to the matching real family and strip the weight,
 * so `<Text style={{ fontWeight: '600' }} />` also gets a real face. Immutable:
 * returns new objects. An explicitly-set `fontFamily` is respected (never
 * overridden), and styles without `fontWeight` pass through unchanged.
 */
export function resolveWeightStyle(
  style: StyleProp<TextStyle>
): StyleProp<TextStyle> {
  if (!style) return style;

  if (Array.isArray(style)) {
    return style.map((entry) => resolveWeightStyle(entry as StyleProp<TextStyle>));
  }

  if (typeof style !== 'object') return style;

  const flat = style as TextStyle;
  if (flat.fontWeight === undefined || flat.fontFamily !== undefined) {
    return style;
  }

  const { fontWeight, ...rest } = flat;
  return { ...rest, fontFamily: familyForWeight(fontWeight) };
}
